import React, {useEffect, useRef, useState} from 'react';
import {Button, Descriptions, Divider, Form, message, Modal, Table, Tag, Typography} from 'antd';
import dayjs from 'dayjs';
import style from '../styles/expenses.salary.statements.table.module.css';
import {FilePdfOutlined} from '@ant-design/icons';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const {Text} = Typography;

const ViewStatementModal = ({visible, onCancel, statementData, module_v}) => {
    const [form] = Form.useForm();
    const modalContentRef = useRef();
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        if (visible && statementData) {
            form.setFieldsValue({
                name: statementData.name,
                description: statementData.description,
                year: dayjs(statementData.date).year(),
                month: dayjs(statementData.date).month() + 1,
                totalAmount: statementData.total_amount,
            });
        }
    }, [visible, statementData, form]);

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    // Функция для экспорта в PDF с использованием html2canvas
    const handleExportToPDF = async () => {
        if (!statementData) {
            message.error('Нет данных для экспорта');
            return;
        }

        if (!modalContentRef.current) {
            message.error('Не удалось получить содержимое для экспорта');
            return;
        }

        setExporting(true);
        try {
            // Создаем canvas из содержимого модального окна
            const canvas = await html2canvas(modalContentRef.current, {
                scale: 2, // Увеличиваем качество
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                removeContainer: true,
                windowWidth: 1200, // Соответствует ширине модального окна
                width: modalContentRef.current.scrollWidth,
                height: modalContentRef.current.scrollHeight,
                onclone: (clonedDoc) => {
                    // Улучшаем стили для PDF
                    const content = clonedDoc.querySelector('.modal-content');
                    if (content) {
                        // Убираем лишние отступы и тени для PDF
                        content.style.boxShadow = 'none';
                        content.style.padding = '10px';

                        // Улучшаем видимость текста
                        const texts = content.querySelectorAll('*');
                        texts.forEach(el => {
                            if (el.style) {
                                el.style.color = '#000000';
                            }
                        });
                    }
                }
            });

            // Создаем PDF
            const pdf = new jsPDF('portrait', 'mm', 'a4');
            const imgData = canvas.toDataURL('image/png');

            // Получаем размеры страницы
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            // Рассчитываем размеры изображения
            const imgWidth = pageWidth - 20; // Отступы по 10мм с каждой стороны
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            let heightLeft = imgHeight;
            let position = 10;

            // Добавляем заголовок перед основным содержимым
            pdf.setFont('times', 'bold');
            pdf.setFontSize(18);
            pdf.text(`SALARY ${dayjs(statementData?.date).format('YYYY-MM')}`, pageWidth / 2, 15, {align: 'center'});

            pdf.setFont('times', 'normal');
            pdf.setFontSize(10);
            pdf.text(`Created: ${dayjs().format('DD.MM.YYYY HH:mm')}`, pageWidth - 10, 15, {align: 'right'});

            position = 25; // Отступ после заголовка

            // Добавляем изображение по частям (если не помещается на одной странице)
            if (heightLeft < pageHeight - position) {
                pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
            } else {
                while (heightLeft > 0) {
                    pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
                    heightLeft -= pageHeight;
                    position -= pageHeight;

                    if (heightLeft > 0) {
                        pdf.addPage();
                        position = 10;
                    }
                }
            }

            // Добавляем подпись на последней странице
            pdf.setFont('times', 'normal');
            pdf.setFontSize(10);

            // Переходим на последнюю страницу
            const totalPages = pdf.internal.getNumberOfPages();
            pdf.setPage(totalPages);

            const signatureY = pageHeight - 20;
            pdf.line(20, signatureY, 80, signatureY);
            pdf.text(`Garden Design APP ${module_v}`, 20, signatureY - 5);

            // Нумерация страниц
            for (let i = 1; i <= totalPages; i++) {
                pdf.setPage(i);
                pdf.setFontSize(9);
                pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 10, pageHeight - 10, {align: 'right'});
            }

            // Сохранение PDF файла
            const fileName = `Ведомость_${statementData.name || 'Без названия'}_${statementData.date ? dayjs(statementData.date).format('YYYY-MM') : dayjs().format('YYYY-MM')}.pdf`;
            pdf.save(fileName);

            message.success('PDF документ успешно создан');
        } catch (error) {
            console.error('Ошибка при создании PDF:', error);
            message.error('Ошибка при создании PDF документа');
        } finally {
            setExporting(false);
        }
    };

    const staffColumns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 50,
            align: 'center',
            render: (text, record) => <div className={style.salary__statements__table__id__text}>{text}</div>
        },
        {
            title: 'ФИО',
            dataIndex: 'fullName',
            key: 'fullName',
        },
        {
            title: 'Наличные (€)',
            dataIndex: ['amounts', 'cash'],
            key: 'cash',
            width: 120,
            render: (cash) => cash?.toFixed(2) || '0.00',
        },
        {
            title: 'Безнал без НДС (€)',
            dataIndex: ['amounts', 'nonCashWithoutVAT'],
            key: 'nonCashWithoutVAT',
            width: 150,
            render: (amount) => amount?.toFixed(2) || '0.00',
        },
        {
            title: 'Безнал с НДС (€)',
            dataIndex: ['amounts', 'nonCashWithVAT'],
            key: 'nonCashWithVAT',
            width: 150,
            render: (amount) => amount?.toFixed(2) || '0.00',
        },
        {
            title: 'Итого (€)',
            key: 'total',
            width: 120,
            render: (_, record) => {
                const amounts = record.amounts || {};
                const total = (amounts.cash || 0) + (amounts.nonCashWithVAT || 0);
                return <Text strong>{total.toFixed(2)}</Text>;
            },
        },
    ];

    return (
        <Modal
            title={`Просмотр ведомости: ${statementData?.name || ''}`}
            open={visible}
            onCancel={handleCancel}
            width={1200}
            footer={[
                <Button
                    key="pdf"
                    icon={<FilePdfOutlined/>}
                    onClick={handleExportToPDF}
                    type="primary"
                    ghost
                    loading={exporting}
                >
                    Экспорт в PDF
                </Button>,
                <Button key="close" onClick={handleCancel}>
                    Закрыть
                </Button>
            ]}
        >
            {/* Контейнер с ref для экспорта */}
            <div ref={modalContentRef} className="modal-content">
                <Form
                    form={form}
                    layout="vertical"
                    disabled
                >
                    <Divider style={{margin: '16px 0'}} titlePlacement="start">Основная информация</Divider>
                    <Descriptions bordered column={2} style={{marginBottom: 24}} size={'small'}>
                        <Descriptions.Item label="ID">
                            <Text strong>{statementData?.id}</Text></Descriptions.Item>
                        <Descriptions.Item label="Статус" disabled={false}>
                            <Tag color={statementData?.status === 'active' ? 'green' : 'red'} disabled={false}>
                                {statementData?.status === 'active' ? 'Активна' : 'Неактивна'}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Название"> <Text
                            strong>{statementData?.name}</Text></Descriptions.Item>
                        <Descriptions.Item label="Период">
                            <Text strong>{dayjs(statementData?.date).format('YYYY-MM')}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Описание" span={2}>
                            <Text strong> {statementData?.description || '—'}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Дата создания">
                            <Text strong>{dayjs(statementData?.created_at).format('DD.MM.YYYY HH:mm')}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Дата обновления">
                            <Text strong> {dayjs(statementData?.updated_at).format('DD.MM.YYYY HH:mm')}</Text>
                        </Descriptions.Item>
                    </Descriptions>

                    <Divider style={{margin: '16px 0'}} titlePlacement="start">Финансовые итоги</Divider>


                    <Descriptions bordered column={4} style={{marginBottom: 24}} size={'small'}>
                        <Descriptions.Item
                            label="Количество сотрудников"><Text
                            strong>{statementData?.employee_count}</Text></Descriptions.Item>
                        <Descriptions.Item label="Общая сумма">
                            <Text strong>{statementData?.total_amount?.toFixed(2)} €</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Наличные"><Text
                            strong>{statementData?.cash_amount?.toFixed(2)}€</Text></Descriptions.Item>
                        <Descriptions.Item label="Безнал с НДС"><Text
                            strong>{statementData?.non_cash_with_vat?.toFixed(2)} €</Text></Descriptions.Item>
                    </Descriptions>
                    <Divider style={{margin: '16px 0'}} titlePlacement="start">Сотрудники в ведомости
                        ({statementData?.staff_details?.length || 0})</Divider>


                    <Table
                        columns={staffColumns}
                        dataSource={statementData?.staff_details || []}
                        rowKey="id"
                        size="small"
                        bordered
                        pagination={false}
                        scroll={{x: 1000}}
                        summary={(pageData) => {
                            const totalCash = pageData.reduce((sum, record) => sum + (record.amounts?.cash || 0), 0);
                            const totalNonCashWithoutVAT = pageData.reduce((sum, record) => sum + (record.amounts?.nonCashWithoutVAT || 0), 0);
                            const totalNonCashWithVAT = pageData.reduce((sum, record) => sum + (record.amounts?.nonCashWithVAT || 0), 0);
                            const total = totalCash + totalNonCashWithVAT;
                            return (
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0} colSpan={2}>
                                        <Text strong>Итого:</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={2}/>
                                    <Table.Summary.Cell index={3}>
                                        <Text strong>{totalCash.toFixed(2)} €</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={4}>
                                        <Text strong>{totalNonCashWithoutVAT.toFixed(2)} €</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={5}>
                                        <Text strong>{totalNonCashWithVAT.toFixed(2)} €</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={6}>
                                        <Text strong>{total.toFixed(2)} €</Text>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            );
                        }}
                    />
                </Form>
            </div>
        </Modal>
    );
};

export default ViewStatementModal;